import { TableOutlined } from '@ant-design/icons';
import { Card, Spin, Typography } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../constants/styles';
import { TabLinkButton } from '../TabLinkButton';

const { Text } = Typography;

const RANGE_COLORS = ['#389e0d', '#faad14', '#fa8c16', '#f5222d'];

function getColor(idx: number): string {
  return RANGE_COLORS[Math.min(idx, RANGE_COLORS.length - 1)];
}

function darkenHex(hex: string): string {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) - 30);
  const g = Math.max(0, ((num >> 8) & 0xff) - 30);
  const b = Math.max(0, (num & 0xff) - 30);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

interface IRow {
  key: number;
  circleLabel: string;
  label: string;
  count: number;
  color: string;
  tooltip: string;
  dataTable: string;
}

export interface IDonorRangeCardProps {
  title: string;
  /**
   * 'retired' — range-based rows (0–N, N–M, M+)
   * 'lapsed'  — threshold-based rows (>N, >M, ...)
   */
  variant: 'retired' | 'lapsed';
  breakpoints: number[];
  labels: string[];
  counts: number[] | undefined;
  loading: boolean;
  contentWidth: number;
  onNavigate: (dataTable: string) => void;
}

export const DonorRangeCard: FC<IDonorRangeCardProps> = ({
  title,
  variant,
  breakpoints,
  labels,
  counts,
  loading,
  contentWidth,
  onNavigate,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.donorRangeCard');
  const sorted = [...breakpoints].sort((a, b) => a - b);

  const rows: IRow[] =
    variant === 'retired'
      ? Array.from({ length: sorted.length + 1 }, (_, i) => {
          const from: number = i === 0 ? 0 : sorted[i - 1];
          const to: number | null = i < sorted.length ? sorted[i] : null;
          const circleLabel: string = to ? `${from}-${to}` : `${from}+`;
          return {
            key: i,
            circleLabel,
            label: labels[i] ?? circleLabel,
            count: counts?.[i] ?? 0,
            color: getColor(i),
            tooltip: t('retiredTooltip', { label: circleLabel }),
            dataTable: `retired-unresponsive-${from}-${to ?? 'plus'}-days`,
          };
        })
      : sorted.map((bp, idx) => ({
          key: bp,
          circleLabel: `>${bp}`,
          label: labels[idx + 1] ?? `>${bp} Days`,
          count: counts?.[idx] ?? 0,
          color: getColor(idx + 1),
          tooltip: t('lapsedTooltip', { bp }),
          dataTable: `lapsed-${bp}-days`,
        }));

  return (
    <Card
      size="small"
      style={{ flex: 1, background: BRAND_COLORS.primary.deepNavy }}
    >
      <Text style={{ color: '#fff', display: 'block', textAlign: 'center' }}>
        {title}
      </Text>
      <div style={{ marginTop: 8 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: BRAND_COLORS.primary.royalBlue,
            border: `1px solid ${BRAND_COLORS.secondary.steelBlue}`,
            borderRadius: 6,
            padding: 16,
            gap: 12,
          }}
        >
          {rows.map((row) => (
            <div
              key={row.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 8,
                padding: '10px 14px',
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: row.color,
                  border: `3px solid ${darkenHex(row.color)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Text
                  style={{
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {row.circleLabel}
                  <br />
                  {t('daysLabel')}
                </Text>
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Text
                    style={{
                      color: BRAND_COLORS.secondary.lightSage,
                      fontSize: 11,
                    }}
                  >
                    {row.label}
                  </Text>
                  <span
                    style={{ color: '#fff', fontSize: 24, fontWeight: 700 }}
                  >
                    {loading ? <Spin size="small" /> : row.count}
                  </span>
                </div>
                <TabLinkButton
                  fontSize={11}
                  iconColor={row.color}
                  icon={<TableOutlined />}
                  label={t('donorListLabel')}
                  onClick={() => onNavigate(row.dataTable)}
                  tooltip={row.tooltip}
                  windowWidth={contentWidth}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
